import * as React from 'react';
import { cn } from '@/lib/utils';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './table';
import { Input } from './Input';
import { Button } from './button';
import { Spinner } from './index';
import { ChevronUp, ChevronDown, ChevronsUpDown, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Search } from 'lucide-react';
import { Checkbox } from './Checkbox';

export interface Column<T> {
  key: string;
  header: string;
  sortable?: boolean;
  width?: string;
  render?: (value: unknown, row: T) => React.ReactNode;
  /**
   * Value used for sorting when the cell is rendered (so the displayed node
   * doesn't have to match a scalar field on the row). Falls back to
   * `row[key]` when omitted. Only consulted for client-side (uncontrolled) sort.
   */
  sortValue?: (row: T) => string | number | boolean | Date | null | undefined;
}

export interface DataTableProps<T> {
  columns: Column<T>[];
  data: T[];
  keyExtractor: (row: T) => string;
  searchable?: boolean;
  searchPlaceholder?: string;
  /**
   * Extra row keys to include in the search beyond the visible columns
   * (e.g. ['id', 'executionId']). Opt-in per table so long random IDs don't
   * make short queries spuriously match everything on other pages.
   */
  searchKeys?: string[];
  onRowClick?: (row: T) => void;
  emptyMessage?: string;
  loading?: boolean;
  className?: string;
  /** Enable pagination with page size options */
  paginated?: boolean;
  /** Default page size (default: 10) */
  defaultPageSize?: number;
  /** Available page size options (default: [10, 25, 50, 100]) */
  pageSizeOptions?: number[];
  /** Enable row selection with checkboxes */
  selectable?: boolean;
  /** Currently selected row keys */
  selectedKeys?: Set<string>;
  /** Callback when selection changes */
  onSelectionChange?: (selectedKeys: Set<string>) => void;
  /**
   * Controlled sort. When `onSort` is provided the table does NOT sort `data`
   * itself — the parent is expected to supply already-ordered rows (e.g. a
   * server-side sort across a paginated result set). `sortColumn` /
   * `sortDirection` drive only the header indicator.
   */
  sortColumn?: string | null;
  sortDirection?: 'asc' | 'desc';
  onSort?: (columnKey: string) => void;
  /**
   * Row density. 'compact' (the default) tightens cell padding and header
   * height so roughly twice the rows fit per viewport; pass 'comfortable'
   * for the roomier legacy spacing.
   */
  density?: 'comfortable' | 'compact';
  /**
   * Minimum table width in px. Below this the wrapper scrolls horizontally
   * instead of crushing columns into ellipsis. Without it a w-full table can
   * never overflow its overflow-auto wrapper, so no horizontal scrollbar
   * ever appears no matter how many columns are squeezed. Defaults to
   * ~110px per column so every table gets readable columns + scroll for free.
   */
  minWidth?: number;
}

export function DataTable<T>({
  columns,
  data,
  keyExtractor,
  searchable = false,
  searchPlaceholder = 'Search...',
  searchKeys,
  onRowClick,
  emptyMessage = 'No data available',
  loading = false,
  className,
  paginated = false,
  defaultPageSize = 10,
  pageSizeOptions = [10, 25, 50, 100],
  selectable = false,
  selectedKeys,
  onSelectionChange,
  sortColumn: controlledSortColumn,
  sortDirection: controlledSortDirection,
  onSort,
  density = 'compact',
  minWidth,
}: DataTableProps<T>) {
  const [searchQuery, setSearchQuery] = React.useState('');
  const [internalSortColumn, setInternalSortColumn] = React.useState<string | null>(null);
  const [internalSortDirection, setInternalSortDirection] = React.useState<'asc' | 'desc'>('asc');
  const [currentPage, setCurrentPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(defaultPageSize);

  // When `onSort` is supplied the parent owns sort state (server-side sort);
  // otherwise the table sorts its own `data` array client-side.
  const isControlledSort = !!onSort;
  const sortColumn = isControlledSort ? (controlledSortColumn ?? null) : internalSortColumn;
  const sortDirection = isControlledSort ? (controlledSortDirection ?? 'asc') : internalSortDirection;

  // Filter data based on search query
  const filteredData = React.useMemo(() => {
    if (!searchQuery) return data;

    const q = searchQuery.toLowerCase();
    const keys = [...columns.map((c) => c.key as string), ...(searchKeys ?? [])];
    return data.filter((row) => {
      return keys.some((key) => {
        const value = (row as any)[key];
        if (value == null) return false;
        return String(value).toLowerCase().includes(q);
      });
    });
  }, [data, columns, searchKeys, searchQuery]);

  // Sort data. In controlled mode the parent already ordered `data`, so we
  // leave it untouched and only reflect the sort state in the header.
  const sortedData = React.useMemo(() => {
    if (isControlledSort || !sortColumn) return filteredData;

    const col = columns.find((c) => c.key === sortColumn);
    const getVal = (row: T) =>
      col?.sortValue ? col.sortValue(row) : (row as any)[sortColumn];

    return [...filteredData].sort((a, b) => {
      const aVal = getVal(a);
      const bVal = getVal(b);

      // Empty values always sort last, regardless of direction.
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;

      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sortDirection === 'asc'
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }

      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
      }

      if (aVal instanceof Date && bVal instanceof Date) {
        return sortDirection === 'asc'
          ? aVal.getTime() - bVal.getTime()
          : bVal.getTime() - aVal.getTime();
      }

      if (typeof aVal === 'boolean' && typeof bVal === 'boolean') {
        return sortDirection === 'asc'
          ? Number(aVal) - Number(bVal)
          : Number(bVal) - Number(aVal);
      }

      return 0;
    });
  }, [filteredData, sortColumn, sortDirection, isControlledSort, columns]);

  // Pagination calculations
  const totalItems = sortedData.length;
  const totalPages = Math.ceil(totalItems / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, totalItems);

  // Apply pagination
  const paginatedData = React.useMemo(() => {
    if (!paginated) return sortedData;
    return sortedData.slice(startIndex, endIndex);
  }, [sortedData, paginated, startIndex, endIndex]);

  // Reset to page 1 when data or search changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, data.length]);

  const handleSort = (columnKey: string) => {
    if (isControlledSort) {
      onSort!(columnKey);
      return;
    }
    if (internalSortColumn === columnKey) {
      setInternalSortDirection(internalSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setInternalSortColumn(columnKey);
      setInternalSortDirection('asc');
    }
  };

  const getCellValue = (row: T, column: Column<T>): React.ReactNode => {
    const value = (row as any)[column.key];
    if (column.render) {
      return column.render(value, row);
    }
    if (value == null) return '-';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    return String(value);
  };

  if (loading) {
    return (
      <div className={cn('flex items-center justify-center py-12', className)}>
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className={cn('space-y-4', className)}>
      {searchable && (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder={searchPlaceholder}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
      )}

      <div className="rounded-md border">
        <Table
          style={{
            minWidth:
              minWidth ?? columns.length * 110 + (selectable ? 40 : 0),
          }}
          className={cn(
            density === 'compact' &&
              '[&_td]:py-1 [&_th]:h-8 [&_th]:whitespace-nowrap'
          )}
        >
          <TableHeader>
            <TableRow>
              {selectable && (
                <TableHead className="w-10">
                  <Checkbox
                    checked={
                      paginatedData.length > 0 &&
                      paginatedData.every((row) => selectedKeys?.has(keyExtractor(row)))
                    }
                    onCheckedChange={(checked) => {
                      if (!onSelectionChange) return;
                      const next = new Set(selectedKeys);
                      if (checked) {
                        paginatedData.forEach((row) => next.add(keyExtractor(row)));
                      } else {
                        paginatedData.forEach((row) => next.delete(keyExtractor(row)));
                      }
                      onSelectionChange(next);
                    }}
                    aria-label="Select all"
                  />
                </TableHead>
              )}
              {columns.map((column) => (
                <TableHead
                  key={column.key}
                  style={{ width: column.width }}
                  className={cn(
                    column.sortable && 'cursor-pointer select-none hover:bg-muted/50'
                  )}
                  onClick={() => column.sortable && handleSort(column.key)}
                >
                  <div className="flex items-center gap-2">
                    {column.header}
                    {column.sortable && (
                      sortColumn === column.key ? (
                        sortDirection === 'asc' ? (
                          <ChevronUp className="h-4 w-4" />
                        ) : (
                          <ChevronDown className="h-4 w-4" />
                        )
                      ) : (
                        <ChevronsUpDown className="h-4 w-4 opacity-30" />
                      )
                    )}
                  </div>
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginatedData.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length + (selectable ? 1 : 0)}
                  className="h-24 text-center text-muted-foreground"
                >
                  {emptyMessage}
                </TableCell>
              </TableRow>
            ) : (
              paginatedData.map((row) => {
                const rowKey = keyExtractor(row);
                const isSelected = selectedKeys?.has(rowKey) ?? false;
                return (
                  <TableRow
                    key={rowKey}
                    className={cn(
                      onRowClick && 'cursor-pointer hover:bg-muted/50',
                      isSelected && 'bg-muted/30'
                    )}
                    onClick={() => onRowClick?.(row)}
                  >
                    {selectable && (
                      <TableCell>
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={(checked) => {
                            if (!onSelectionChange) return;
                            const next = new Set(selectedKeys);
                            if (checked) {
                              next.add(rowKey);
                            } else {
                              next.delete(rowKey);
                            }
                            onSelectionChange(next);
                          }}
                          onClick={(e) => e.stopPropagation()}
                          aria-label={`Select row ${rowKey}`}
                        />
                      </TableCell>
                    )}
                    {columns.map((column) => (
                      <TableCell key={column.key}>
                        {getCellValue(row, column)}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination Controls */}
      {paginated && totalItems > 0 && (
        <div className="flex flex-col gap-3 px-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span>Rows per page:</span>
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="h-8 w-16 rounded border border-border bg-background px-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            >
              {pageSizeOptions.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span>
              {startIndex + 1}-{endIndex} of {totalItems}
            </span>
          </div>

          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              className="h-8 w-8 p-0"
            >
              <ChevronsLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="h-8 w-8 p-0"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="px-2 text-sm">
              Page {currentPage} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="h-8 w-8 p-0"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
              className="h-8 w-8 p-0"
            >
              <ChevronsRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
